import {debounce} from '@github/mini-throttle'
import {FileFilterShared, type FileFilterBaseProps} from '@github-ui/diff-file-tree/file-filter'
import {useRef} from 'react'
import {ActionList} from '@primer/react'
// eslint-disable-next-line no-restricted-imports
import type {PullRequestFileTreeDiff} from '@github-ui/pull-requests/page-data/payloads/file-tree'
import {useSearchParams} from '@github-ui/use-navigate'

export type FileFilterState = {
  filterText: string
  fileExtensions: Record<string, number>
  unselectedFileExtensions: Set<string>
  showDeletedFiles?: boolean
  showOnlyManifestFiles?: boolean
  showOnlyOwnedFiles?: boolean
  showVendoredFiles?: boolean
  showViewedFiles?: boolean
}

export type FileFilterMenuOptions = {
  canSeeCodeownersFilter: boolean
  canSeeDeletedFilesFilter: boolean
  canSeeOnlyManifestFilesFilter: boolean
  canSeeVendorFilesFilter: boolean
}

export type FileFilterProps = {
  diffs: Readonly<Array<Readonly<PullRequestFileTreeDiff>>>
  fileFilterMenuOptions: FileFilterMenuOptions
  fileFilterState: FileFilterState
  setFileFilterState: (state: FileFilterState) => void
} & Omit<FileFilterBaseProps, 'onFilterChange'>

export function FileFilter({diffs, fileFilterMenuOptions, fileFilterState, setFileFilterState}: FileFilterProps) {
  const [searchParams, setSearchParams] = useSearchParams()

  const {
    filterText,
    fileExtensions,
    unselectedFileExtensions,
    showOnlyOwnedFiles,
    showDeletedFiles,
    showOnlyManifestFiles,
    showVendoredFiles,
    showViewedFiles,
  } = fileFilterState

  const {canSeeCodeownersFilter, canSeeDeletedFilesFilter, canSeeOnlyManifestFilesFilter, canSeeVendorFilesFilter} =
    fileFilterMenuOptions

  const debouncedOnSearch = useRef(
    debounce(
      (newQuery: string) =>
        setFileFilterState({
          ...fileFilterState,
          filterText: newQuery,
        }),
      250,
    ),
  )

  const onFileExtensionChange = (
    type: 'selectFileExtension' | 'unselectFileExtension',
    payload: {
      extension: string
    },
  ) => {
    let newUnselectedFileExtensions = unselectedFileExtensions

    if (type === 'selectFileExtension') {
      newUnselectedFileExtensions = new Set([...unselectedFileExtensions].filter(ext => ext !== payload.extension))
    } else if (type === 'unselectFileExtension') {
      newUnselectedFileExtensions = new Set([...unselectedFileExtensions, payload.extension])
    }

    const allFileExtensions = Object.keys(fileExtensions)
    const selectedFileExtensions = allFileExtensions.filter(extension => !newUnselectedFileExtensions.has(extension))

    // Clean slate
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.delete('file-filters[]')
    // Add selected file extensions
    selectedFileExtensions.map(extension => {
      newSearchParams.append('file-filters[]', extension)
    })

    onFilterChange(newSearchParams, {unselectedFileExtensions: newUnselectedFileExtensions})
  }

  const onFilterChange = (
    newSearchParams: URLSearchParams,
    changedFileFilterState: {[key: string]: Set<string> | boolean},
  ) => {
    setSearchParams(newSearchParams, {preventAutofocus: true})
    setFileFilterState({
      ...fileFilterState,
      ...changedFileFilterState,
    })
  }

  return (
    <FileFilterShared
      filterText={filterText}
      fileExtensions={fileExtensions}
      unselectedFileExtensions={unselectedFileExtensions}
      onFilterTextChange={text => debouncedOnSearch.current(text)}
      onFilterChange={onFileExtensionChange}
      additionalFilterGroups={
        <>
          {canSeeCodeownersFilter && (
            <>
              <ActionList.Divider />
              <ActionList.Group selectionVariant="single">
                <ActionList.Item
                  selected={showOnlyOwnedFiles}
                  onSelect={() => {
                    const newSearchParams = new URLSearchParams(searchParams)
                    newSearchParams.set('owned-by', `${!showOnlyOwnedFiles}`)
                    onFilterChange(newSearchParams, {showOnlyOwnedFiles: !showOnlyOwnedFiles})
                  }}
                >
                  {`Only files owned by you (${diffs.filter(diff => diff.isCodeowner).length})`}
                </ActionList.Item>
              </ActionList.Group>
            </>
          )}
          <ActionList.Divider />
          <ActionList.Group selectionVariant="single">
            {canSeeOnlyManifestFilesFilter && (
              <ActionList.Item
                selected={showOnlyManifestFiles}
                onSelect={() => {
                  const newSearchParams = new URLSearchParams(searchParams)
                  newSearchParams.set('manifests', `${!showOnlyManifestFiles}`)
                  onFilterChange(newSearchParams, {showOnlyManifestFiles: !showOnlyManifestFiles})
                }}
              >
                Only manifest files
              </ActionList.Item>
            )}
            {canSeeDeletedFilesFilter && (
              <ActionList.Item
                selected={showDeletedFiles}
                onSelect={() => {
                  const newSearchParams = new URLSearchParams(searchParams)
                  newSearchParams.set('show-deleted-files', `${!showDeletedFiles}`)
                  onFilterChange(newSearchParams, {showDeletedFiles: !showDeletedFiles})
                }}
              >
                Deleted files
              </ActionList.Item>
            )}
            {canSeeVendorFilesFilter && (
              <ActionList.Item
                selected={showVendoredFiles}
                onSelect={() => {
                  const newSearchParams = new URLSearchParams(searchParams)
                  newSearchParams.set('show-vendored-files', `${!showVendoredFiles}`)
                  onFilterChange(newSearchParams, {showVendoredFiles: !showVendoredFiles})
                }}
              >
                Vendored files
              </ActionList.Item>
            )}
            <ActionList.Item
              selected={showViewedFiles}
              onSelect={() => {
                const newSearchParams = new URLSearchParams(searchParams)
                newSearchParams.set('show-viewed-files', `${!showViewedFiles}`)
                onFilterChange(newSearchParams, {showViewedFiles: !showViewedFiles})
              }}
            >
              Viewed files
            </ActionList.Item>
          </ActionList.Group>
        </>
      }
    />
  )
}

try{ FileFilter.displayName ||= 'FileFilter' } catch {}